import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import OrderLineItemsList from '../../components/alert-screens/shared/order-line-item/OrderLineItemsList';
import ReactDOM from 'react-dom';
import { RemoveScroll } from 'react-remove-scroll';
import { useTranslation } from 'react-i18next';
import OrderSummary from '../../components/alert-screens/shared/OrderSummary';
import { ReceiptType } from '../../providers/PrintProvider';
import usePrinter from '../../hooks/usePrinter';
import OrderFulfillmentsManager from '../../services/api/OrderFulfillmentsManager';
import { toast } from 'react-toastify';
import { useOngoingOrders } from '../../providers/OngoingOrdersProvider';
import useSound from '../../hooks/utility/useSound';
import { ClockCircleOutlined, PrinterOutlined } from '@ant-design/icons';
import { useHelpers } from '../../providers/HelpersProvider';
import classnames from 'classnames';
import OrderHelpers from '../../services/helpers/OrderHelpers';
import TableBox, { TableBoxVariant } from '../../components/order/TableBox';
import useTableLabel from '../../hooks/useTableLabel';

const Label = ({ children, className }) => (
  <div
    className={classnames(
      'border border-blue-500/30 bg-blue-500/10 text-blue-500 gap-xs flex justify-center items-center py-sm rounded-lg text-center',
      className,
    )}
  >
    {children}
  </div>
);

const OrderPickupTimeUpdatedAlertScreen = ({ order }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Views:Alerts:OrderPickupTimeUpdatedAlertScreen' });

  const { playSound } = useSound();
  const { formatTime } = useHelpers();
  const { replaceOrderData } = useOngoingOrders();
  const { print, printing } = usePrinter();
  const [loading, setLoading] = useState(false);
  const isOnSite = OrderHelpers.isOnSite(order);
  const tableLabel = useTableLabel();

  useEffect(() => {
    playSound();
    const id = setInterval(() => playSound(), 10000);

    return () => clearInterval(id);
  }, []);

  const onConfirm = async (shouldPrint = false) => {
    setLoading(true);
    const { success, data } = await OrderFulfillmentsManager.update(order.fulfillment_id, {
      is_pickup_time_change_handled: true,
    });

    if (!success) {
      toast.error(t('toasts.something_went_wrong'));

      return setLoading(false);
    }

    replaceOrderData(data.data);
    shouldPrint && print({ type: ReceiptType.Order, payload: data.data });
    setLoading(false);
  };

  return ReactDOM.createPortal(
    <RemoveScroll enabled className="w-full inset-0 fixed z-[1200] flex justify-center bg-blue-500 lg:p-md p-xs">
      <div className="max-h-full bg-white flex flex-col justify-center space-y-md w-full lg:w-3/5 rounded-lg m-auto p-md">
        <div className="bg-blue-500/10 rounded-full flex justify-center items-center p-md w-fit mx-auto">
          <ClockCircleOutlined className="text-md text-blue-500" />
        </div>
        <div className="flex-1 overflow-y-auto space-y-sm">
          <h4 className="text-center uppercase font-bold text-blue-500">{t('title')}</h4>
          {isOnSite && <TableBox variant={TableBoxVariant.Alert} label={tableLabel(order)} />}
          <OrderSummary order={order} />
          <div className="space-y-xs flex flex-col">
            <span className="block text-gray-500 mx-auto">
              {t(isOnSite ? 'labels.old_serve_time' : 'labels.old_time')}
            </span>
            <div className="bg-gray-100 text-gray-500 line-through flex justify-center items-center py-sm rounded-lg">
              {formatTime(order.initial_estimated_pickup_time)}
            </div>
          </div>
          <div className="space-y-xs flex flex-col">
            <span className="block text-gray-600 mx-auto">
              {t(isOnSite ? 'labels.new_serve_time' : 'labels.new_time')}
            </span>
            <Label className="text-md font-bold">{formatTime(order.estimated_pickup_time)}</Label>
          </div>
          <Label className="text-sm">{t('feedback.new_prep_time')}</Label>
          <OrderLineItemsList order={order} />
        </div>
        <div className="sticky bottom-0 space-y-sm">
          <Button
            size="large"
            icon={<PrinterOutlined />}
            className="hover:!border-blue-500/75 hover:!text-blue-500/75 w-full"
            loading={loading || printing}
            onClick={() => onConfirm(true)}
          >
            {t('buttons.print')}
          </Button>
          <Button
            variant="solid"
            className="bg-blue-500 !text-white hover:!bg-blue-500/75 w-full"
            size="large"
            loading={loading}
            onClick={() => onConfirm()}
          >
            {t('buttons.got_it')}
          </Button>
        </div>
      </div>
    </RemoveScroll>,
    document.body,
  );
};

export default OrderPickupTimeUpdatedAlertScreen;
